import { Component, Input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { NgSelectComponent } from '@ng-select/ng-select';
import { ControlsOf, FormArray, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, first } from 'rxjs/operators';
import { PlanningHasEntity } from 'src/app/classes/planning-has-entity.class';
import { Planning } from 'src/app/classes/planning.class';
import { UserPlanning } from 'src/app/classes/user-planning';
import { User } from 'src/app/classes/user.class';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog-service/confirm-dialog.service';
import { LocationService } from 'src/app/services/location.service';
import { PlanningHasService } from 'src/app/services/planning/planning-has.service';
import { UserPlanningService } from 'src/app/services/planning/user-planning.service';
import { UserService } from 'src/app/services/user/user.service';
import { Utils } from 'src/app/utils.class';
import { localEmployeeAvailableValidator } from 'src/app/validators/local-employee-available.validator';
import { environment } from 'src/environments/environment';
import { AddressDialogComponent } from './address-dialog/address-dialog.component';

@Component({
    selector: 'app-planning-detail-employees',
    templateUrl: './planning-detail-employees.component.html',
    styleUrls: ['./planning-detail-employees.component.scss'],
    standalone: false,
})
export class PlanningDetailEmployeesComponent implements OnInit, OnDestroy, OnChanges {
    saving: boolean;
    form: FormArray<ControlsOf<FormGroupUserPlanning>>;
    employees: User[];
    filteredEmployees: User[];
    sums: { function: string; amount: number }[] = [];
    fcEmployee = new FormControl<number>();
    @Input() planning: Planning;
    @Input() planningForm: FormGroup<any>;
    @Input() formsDisabled: boolean;
    @Input() fixedDate: Date;
    filterFunction: string | null = null;
    @ViewChild('addEmployeeSelect') addEmployeeSelect: NgSelectComponent;

    PlanningStatus = environment.status.Planning;
    mainPlanning: PlanningHasEntity;
    private subscriptions = new Subscription();

    constructor(
        private dialog: MatDialog,
        private confirmDialog: ConfirmDialogService,
        private userPlanningService: UserPlanningService,
        private locationService: LocationService,
        private userService: UserService,
        private planningHasService: PlanningHasService
    ) {}

    ngOnInit(): void {
        this.form = new FormArray([]);
        this.subscriptions.add(
            this.planningForm.get('asphalt_performer').valueChanges.subscribe((newPerformerId: number) => {
                const previousPerformerId = this.planningForm.value['asphalt_performer'];
                if (previousPerformerId !== newPerformerId) {
                    const origFg = this.form.controls.find(fg => fg.value.user_id === previousPerformerId);
                    this.form.controls.splice(this.form.controls.indexOf(origFg), 1);

                    if (
                        !this.form.controls.some(fg => fg.value.user_id === newPerformerId) &&
                        this.employees.some(e => e.id === newPerformerId)
                    ) {
                        this.addEmployee(newPerformerId);
                    }
                }
            })
        );
        this.subscriptions.add(
            this.planningForm
                .get('location')
                .valueChanges.pipe(distinctUntilChanged())
                .subscribe((newLocation: string) => {
                    const previousLocation = this.planningForm.value['location'];

                    this.form.controls.forEach(fg => {
                        if (fg.controls.work_begin.value === previousLocation) {
                            fg.controls.work_begin.setValue(newLocation);
                        }
                        if (fg.controls.work_end.value === previousLocation) {
                            fg.controls.work_end.setValue(newLocation);
                        }
                    });
                })
        );

        this.subscriptions.add(
            this.planningForm.get('status_key').valueChanges.subscribe((newStatusId: string) => {
                const finalStatuses = [this.PlanningStatus.PlanningDefinitief, this.PlanningStatus.PlanningBevestigd];
                if (
                    !finalStatuses.includes(this.planningForm.value['status_key']) &&
                    finalStatuses.includes(newStatusId)
                ) {
                    this.addDefaults();
                }
            })
        );

        this.subscriptions.add(
            this.planningForm
                .get('date')
                .get('begintime')
                .valueChanges.subscribe((newStartTime: Date) => {
                    const previousTime = this.planningForm.value['date']['begintime'];
                    this.form.controls.forEach(fg => {
                        if (previousTime === Utils.getTimeOrNull(fg.value.begindate)) {
                            fg.controls.begindate.setValue(new Date(newStartTime));
                        }
                    });
                })
        );
        this.subscriptions.add(
            this.planningForm
                .get('date')
                .get('endtime')
                .valueChanges.subscribe((newEndTime: Date) => {
                    const previousTime = this.planningForm.value['date']['endtime'];
                    this.form.controls.forEach(fg => {
                        if (previousTime === Utils.getTimeOrNull(fg.value.enddate)) {
                            fg.controls.enddate.setValue(new Date(newEndTime));
                        }
                    });
                })
        );

        this.subscriptions.add(
            this.userService
                .getList(false, false)
                .pipe(first())
                .subscribe(users => {
                    this.employees = users;
                    this.planning.user_planning
                        ?.filter(userPlanning => this.shouldShowUserPlanning(userPlanning))
                        .forEach(userPlanning => {
                            const fg = new FormGroup<ControlsOf<FormGroupUserPlanning>>(
                                {
                                    id: new FormControl(userPlanning.id),
                                    user_id: new FormControl(+userPlanning.user_id),
                                    planning_id: new FormControl(userPlanning.planning_id),
                                    begindate: new FormControl(userPlanning.begindate),
                                    enddate: new FormControl(userPlanning.enddate),
                                    conflictText: new FormControl(''),
                                    comment: new FormControl(userPlanning.comment),
                                    function: new FormControl(
                                        this.employees.find(e => e.id === userPlanning.user_id)?.function
                                    ),
                                    employee: new FormControl(
                                        this.employees.find(e => e.id === userPlanning.user_id)?.name
                                    ),
                                    updated_at: new FormControl(userPlanning.updated_at),
                                    origin: new FormControl(userPlanning.origin),
                                    destination: new FormControl(userPlanning.destination),
                                    work_begin: new FormControl(userPlanning.work_begin),
                                    work_end: new FormControl(userPlanning.work_end),
                                },
                                [],
                                localEmployeeAvailableValidator(
                                    this.userPlanningService,
                                    this.planningHasService,
                                    userPlanning._movedFrom
                                )
                            );
                            this.subscriptions.add(
                                fg.controls.begindate.valueChanges.subscribe(() => {
                                    fg.controls.enddate.updateValueAndValidity();
                                })
                            );
                            this.subscriptions.add(
                                fg.controls.enddate.valueChanges.subscribe(enddate => {
                                    const begindate = new Date(fg.controls.begindate.value);
                                    enddate = new Date(enddate);
                                    begindate.setSeconds(0);
                                    const endTime = new Date(begindate);
                                    endTime.setHours(enddate.getHours());
                                    endTime.setMinutes(enddate.getMinutes());

                                    if (endTime < begindate && enddate <= begindate) {
                                        enddate.setDate(enddate.getDate() + 1);
                                        fg.controls.enddate.setValue(enddate, { emitEvent: false, onlySelf: true });
                                    }
                                    if (endTime > begindate && enddate.getDate() !== begindate.getDate()) {
                                        enddate.setDate(begindate.getDate());
                                        fg.controls.enddate.setValue(enddate, { emitEvent: false, onlySelf: true });
                                    }
                                })
                            );
                            this.form.push(fg);
                        });
                    this.calculateSums();
                    this.disableorEnableForms();
                    this.mainPlanning = Utils.mainPlanning(this.planning);
                })
        );
    }

    addDefaults() {
        this.filteredEmployees
            .filter(
                e =>
                    this.mainPlanning?.entity?.default_employees?.includes(e.id) &&
                    !this.form.value.map(c => c.user_id).includes(e.id)
            )
            .forEach(user => {
                this.addEmployee(user.id);
            });
    }

    ngOnChanges() {
        this.disableorEnableForms();
    }

    public reValidateFields(daysDiff = 0) {
        this.form.controls.forEach(userPlanningForm => {
            if (daysDiff) {
                const newBeginDate = new Date(userPlanningForm.value.begindate);
                newBeginDate.setDate(newBeginDate.getDate() + daysDiff);
                userPlanningForm.controls.begindate.setValue(newBeginDate);

                const newEndDate = new Date(userPlanningForm.value.enddate);
                newEndDate.setDate(newEndDate.getDate() + daysDiff);
                userPlanningForm.controls.enddate.setValue(newEndDate);
            }
            return Utils.triggerValidationP(userPlanningForm);
        });
    }

    selectAddress(control: FormGroup<ControlsOf<FormGroupUserPlanning>>) {
        if (this.formsDisabled) {
            return;
        }
        this.dialog.open(AddressDialogComponent, {
            width: '700px',
            height: '530px',
            maxHeight: '600px',
            data: {
                control,
                user: this.employees.find(e => e.id === control.value.user_id),
                location: this.planning.location,
            },
        });
    }

    filterEmployees() {
        const currentEmployees = this.form.value.map(e => e.user_id);
        this.filteredEmployees = this.employees.filter(e => currentEmployees.indexOf(e.id) === -1);
    }

    calculateSums() {
        setTimeout(() => {
            this.filterEmployees();
            this.sums = [];
            if (this.planning.traffic_controllers > 0) {
                this.sums.push({
                    function: 'Verkeersregelaar',
                    amount: 0,
                });
            }
            this.form.value.forEach(employee => {
                const sum = this.sums.find(p => p.function == employee.function);
                if (sum) {
                    sum.amount++;
                } else {
                    this.sums.push({
                        function: employee.function,
                        amount: 1,
                    });
                }
            });
        });
    }

    addEmployee(userId: number) {
        const fg = new FormGroup<ControlsOf<FormGroupUserPlanning>>(
            {
                user_id: new FormControl(userId),
                planning_id: new FormControl(this.planning.id),
                begindate: new FormControl(Utils.mainPlanning(this.planning).begindate, Validators.required),
                enddate: new FormControl(Utils.mainPlanning(this.planning).enddate, Validators.required),
                conflictText: new FormControl(),
                comment: new FormControl(),
                function: new FormControl(this.employees.find(e => e.id === userId)?.function),
                employee: new FormControl(this.employees.find(e => e.id === userId)?.name),
                updated_at: new FormControl(),
                origin: new FormControl(
                    this.locationService.formatAddress(this.employees.find(u => u.id === userId) as any)
                ),
                destination: new FormControl(
                    this.locationService.formatAddress(this.employees.find(u => u.id === userId) as any)
                ),
                work_begin: new FormControl(this.planning.location),
                work_end: new FormControl(this.planning.location),
            },
            [],
            localEmployeeAvailableValidator(this.userPlanningService, this.planningHasService)
        );
        this.fcEmployee.reset();
        this.form.push(fg);
        this.filterFunction = null;
        this.calculateSums();
        this.disableorEnableForms();
    }

    deleteAll() {
        this.confirmDialog
            .confirm({
                title: 'Verwijderen',
                text: `Weet u zeker dat u al het pesoneel wilt verwijderen van deze planning?`,
                confirm: 'Verwijderen',
                cancel: 'Behouden',
            })
            .then(
                () => {
                    this.form.controls = [];
                    this.calculateSums();
                },
                () => {}
            );
    }

    delete(formgroup: FormGroup<ControlsOf<FormGroupUserPlanning>>) {
        this.confirmDialog
            .confirm({
                title: 'Verwijderen',
                text: `Weet u zeker dat u ${formgroup.value.employee} wilt verwijderen van de planning?`,
                confirm: 'Ja',
                cancel: 'Nee',
            })
            .then(
                () => {
                    this.form.controls.splice(this.form.controls.indexOf(formgroup), 1);
                    this.calculateSums();
                },
                () => {}
            );
    }

    disableorEnableForms() {
        if (this.formsDisabled) {
            this.form?.controls.forEach(form => form.disable());
        } else {
            this.form?.controls.forEach(form => form.enable());
        }
    }

    validateForms(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            if (this.formsDisabled) {
                resolve(true);
            }
            Promise.all([...this.form.controls.map(employeeForm => Utils.triggerValidationP(employeeForm))]).then(
                () => {
                    resolve(true);
                },
                () => {
                    this.form.controls.forEach(employeeForm => {
                        if (!employeeForm.valid) {
                            for (const name of Object.keys(employeeForm.controls)) {
                                employeeForm.controls[name].markAsTouched();
                            }
                        }
                    });
                    reject();
                }
            );
        });
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    /**
     * Shows planning without a performer, assigned to the employee themselves, their direct hours checker,
     * or this planning's regular/asphalt performer. Planning assigned to another performer is filtered out.
     */
    private shouldShowUserPlanning(userPlanning: UserPlanning): boolean {
        const user = this.employees.find(employee => employee.id === userPlanning.user_id);
        if (
            userPlanning.performer_id == null ||
            userPlanning.performer_id === userPlanning.user_id ||
            userPlanning.performer_id === user?.hours_checked_by
        ) {
            return true;
        }
        return [this.planning?.performer, this.planning?.asphalt_performer]
            .filter((performerId): performerId is number => performerId != null)
            .includes(userPlanning.performer_id);
    }
}

export interface FormGroupUserPlanning extends Omit<
    UserPlanning,
    'begindate' | 'enddate' | 'begintime' | 'endtime' | 'updated_at'
> {
    updated_at: FormControl<Date>;
    begindate: FormControl<Date>;
    enddate: FormControl<Date>;
    function: string;
    employee: string;
    conflictText: string;
}
